/* eslint-disable no-use-before-define */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormControl } from '@mui/material';
import PropTypes from 'prop-types';

import { userService } from '../../../../../services/napoleon';
import { updateUserId } from '../../../../../redux/actions/userActions';
import useToastActions from '../../../../../redux/actions/useToastActions';
import { REGISTER_STEPS } from '../../../../../constants/registerSteps';
import ButtonComponent from '../../../../../components/ButtonComponent';
import Input from '../../../../../components/Form/Input';
import { isValidCPF, isValidCellphone } from '../../../../../utils';
import styles, { FormContainer, Footer } from '../../styles';
import { errorMessages } from './helpers';
import useFormStep from './useFormStep';
import StepsHeadline from './StepsHeadline';
import Checkbox from '../../../../../components/checkbox';

const formStep = REGISTER_STEPS.USER_INFO;
const notMandatoryFields = ['phone'];

export default function UserInfo({ handleBack, handleNext, disableBack = false }) {
  const { error } = useToastActions();
  const dispatch = useDispatch();

  const { isAuthenticated } = useSelector((state) => state.userReducer);
  const { authByGoogle, steps, userId } = useSelector((state) => state.registerStepReducer);

  const [valueCheckBox, setValueCheckBox] = useState(false);

  const signInInfo = steps[REGISTER_STEPS.SIGN_IN_INFO];

  const currentStepData = steps[formStep] || {};
  const hasCompletedRegister = !!(currentStepData.name && currentStepData.cpf);

  const {
    form,
    formErrors,
    isLoading,
    inputRef,
    handleChange,
    handleSubmit,
    updateRedux,
    isStepValid,
  } = useFormStep(formStep, validateField, registerUser);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  async function registerUser() {
    const auth = authByGoogle?.authGoogle;
    const payload = {
      ...signInInfo, ...form, authByGoogle: auth, acceptsSms: valueCheckBox,
    };

    try {
      if (!hasCompletedRegister) {
        if (!isAuthenticated) {
          const { data } = await userService.create(payload);
          dispatch(updateUserId(data.id));
        } else {
          await userService.update({ ...payload, id: userId });
        }
        updateRedux();
      }

      handleNext();
    } catch (err) {
      const responseError = err?.response?.data?.message?.toLowerCase() || '';
      const knownErrors = [
        { match: 'email already in use', message: 'Email já cadastrado.' },
        { match: 'cpf already in use', message: 'CPF já cadastrado.' },
      ];
      const toastError = knownErrors.find(({ match }) => responseError.includes(match))?.message || 'Erro ao tentar salvar dados do usuário. Tente novamente mais tarde.';
      error(toastError);
    }
  }

  function validateField(name, value) {
    if (notMandatoryFields.includes(name) && !value?.trim()) return '';
    if (!value?.trim()) return errorMessages.MANDATORY_FIELD;

    switch (name) {
      case 'cpf':
        if (!isValidCPF(value)) return 'CPF inválido';
        break;
      case 'name':
        if (value.length < 2) return 'Nome deve ter pelo menos 2 caracteres';
        if (value.length > 200) return 'Nome deve ter no máximo 200 caracteres';
        break;
      case 'phone':
        if (!isValidCellphone(value)) return 'Celular inválido';
        break;
      default:
        return '';
    }
    return '';
  }

  return (
    <FormContainer onSubmit={handleSubmit}>
      <StepsHeadline />

      <FormControl sx={styles.textField} variant="standard">
        <Input
          ref={inputRef}
          placeholder="Nome completo"
          value={form.name}
          onChange={handleChange}
          type="text"
          name="name"
          inputProps={{ maxLength: 200 }}
          error={!!formErrors.name}
          helperText={formErrors.name}
          disabled={isLoading}
        />
      </FormControl>

      <FormControl sx={styles.textField} variant="standard">
        <Input
          placeholder="CPF"
          value={form.cpf}
          onAccept={(value) => handleChange({ target: { name: 'cpf', value } })}
          type="tel"
          name="cpf"
          mask="000.000.000-00"
          error={!!formErrors.cpf}
          helperText={formErrors.cpf}
          disabled={isLoading}
        />
      </FormControl>

      <FormControl sx={styles.textField} variant="standard">
        <Input
          placeholder="Celular (opcional)"
          value={form.phone}
          onAccept={(value) => handleChange({ target: { name: 'phone', value } })}
          type="tel"
          name="phone"
          mask="(00) 00000-0000"
          error={!!formErrors.phone}
          helperText={formErrors.phone}
          disabled={isLoading}
        />
      </FormControl>

      <FormControl sx={styles.textField} variant="standard">
        <Checkbox
          label="Aceito receber notificações por SMS"
          checked={valueCheckBox}
          onChange={() => {
            setValueCheckBox(!valueCheckBox);
          }}
        />
      </FormControl>

      <Footer style={styles.registerStepsFooter}>
        {!isAuthenticated && (
          <ButtonComponent
            size="small"
            variant="outlined"
            disabled={disableBack || isLoading}
            onClick={() => {
              updateRedux();
              handleBack();
            }}
          >
            Voltar
          </ButtonComponent>
        )}

        <ButtonComponent
          type={isLoading ? 'button' : 'submit'}
          size={isAuthenticated ? 'large' : 'small'}
          disabled={!isStepValid() || isLoading}
          loading={isLoading}
        >
          {hasCompletedRegister ? 'Próximo' : 'Concluir'}
        </ButtonComponent>
      </Footer>
    </FormContainer>
  );
}

UserInfo.propTypes = {
  handleBack: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired,
};
